/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
 */
/**
 * @param {string} str
 * @returns {string}
 */
export const camelCaseToSpacedString = str =>
    str
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

/**
 * @param {string} timestamp
 * @param {boolean} toDate
 * @returns {string}
 */
export const timestampToDate = (timestamp, toDate = false) => {
    if (!toDate) return timestamp;
    const date = new Date(timestamp);

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    return `${year}-${month.toString().padStart(2, '0')}-${day
        .toString()
        .padStart(2, '0')} ${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * @param {array} entityDetails
 * @param {object} filters
 * @param {boolean} toDate
 * @returns {array}
 */
export const mapFilters = (entityDetails, filters, toDate = false) =>
    entityDetails
        .map(detail => {
            const isDate = detail.fieldType === 'date';
            const isBoolean = detail.fieldType === 'boolean';
            const isKeyword = detail.fieldType === 'keyword';

            if (isBoolean) {
                // for boolean fields we need to distinguish between unset field (value='') and false value
                // (although they are both falsy) If unset, the filter should not be sent to the backend
                if (filters[detail.fieldName].value === '') return null;
                return {
                    field: detail.fieldName,
                    value: filters[detail.fieldName].value, // value can be true or false here. Both values are sent to the backend
                    fieldType: detail.fieldType
                };
            } else if (isKeyword && filters[detail.fieldName].value) {
                // these are multiselects, so multiple values can be selected (value should be an array)
                const value = filters[detail.fieldName].value;
                if ((Array.isArray(value) && value.length === 0) || (!Array.isArray(value) && value === ''))
                    return null;
                return {
                    field: detail.fieldName,
                    value: Array.isArray(value) ? value : [value],
                    fieldType: detail.fieldType
                };
            } else if (filters[detail.fieldName].from) {
                return {
                    field: detail.fieldName,
                    from: timestampToDate(filters[detail.fieldName].from, isDate && toDate),
                    to: timestampToDate(filters[detail.fieldName].to, isDate && toDate),
                    fieldType: detail.fieldType
                };
            } else if (filters[detail.fieldName].value) {
                return {
                    field: detail.fieldName,
                    value: filters[detail.fieldName].value,
                    fieldType: detail.fieldType
                };
            }
            return null;
        })
        .filter(Boolean);

/**
 * @param {array} filters
 * @returns {object}
 */
export const mapToFilters = filters => {
    const newFilters = {};
    const filterFieldNames = {};

    filters.forEach(({ field, value = '', from = '', to = '' }) => {
        newFilters[field] = {
            value,
            from,
            to
        };
        filterFieldNames[field] = true;
    });

    return {
        filters: newFilters,
        filterFieldNames
    };
};

/**
 * @param {object} displayedFieldNames
 * @param {object} subFieldNames
 * @returns {array}
 */
export const mapFields = (displayedFieldNames, subFieldNames) =>
    Object.keys(displayedFieldNames)
        .filter(field => displayedFieldNames[field])
        .map(field => (subFieldNames[field] ? `${field}.${subFieldNames[field]}` : field));

/**
 * @param {array} entityDetails
 * @param {object} displayedFieldNames
 * @returns {array}
 */
export const mapFormats = (entityDetails, displayedFieldNames) =>
    entityDetails
        .filter(field => field.fieldType === 'date' && displayedFieldNames[field.fieldName])
        .map(field => ({ field: field.fieldName, type: field.fieldType }));

/**
 * @param {array} fields
 * @returns {object}
 */
export const mapToFields = fields => {
    const newFields = {};
    fields.forEach(field => (newFields[field] = true));
    return newFields;
};

export const dateAggregationOptions = {
    '1d': 'day',
    '1w': 'week',
    '1M': 'month',
    '1q': 'quarter',
    '1y': 'year'
};

/**
 * This function ensure the returned array is flatten and with unique values
 * @param {array} options
 * @returns {array}
 */
export const flattenUniqueDropdownOptions = options => {
    const flatten = array => array.flatMap(item => item);
    return Array.from(new Set(flatten(options)));
};
